import { Action } from "../Actions";
import { ContactTypes } from "../../constants";
import { findActiveContactIndex } from "./helpers";
import { isNumber } from "lodash";
import moment from "moment";

const LOAD_TECHNICIAN_CONTACTS = "LOAD_TECHNICIAN_CONTACTS";
const SET_ACTIVE_TECHNICIAN_CONTACT = "SET_ACTIVE_TECHNICIAN_CONTACT";
const SET_ACTIVE_TECH_CONTACT_BY_SYSTEM_ID =
  "SET_ACTIVE_TECH_CONTACT_BY_SYSTEM_ID";
const TYPE_TECHNICIAN_SEARCH_STRING = "TYPE_TECHNICIAN_SEARCH_STRING";
const CHANGE_VISIBILITY = "CHANGE_VISIBILITY";
const TECHNICIAN_LOAD_MESSAGES = "TECHNICIAN_LOAD_MESSAGES";
const MESSAGE_WAS_SENT = "MESSAGE_WAS_SENT";
const TECH_UPDATE_INCOMING_COUNTER = "TECH_UPDATE_INCOMING_COUNTER";
const TECHNICIAN_UPDATE_MESSAGE_STATUS = "TECHNICIAN_UPDATE_MESSAGE_STATUS";

// Come from MessagesActions
const CHANGE_CONTACT_TYPE = "CHANGE_CONTACT_TYPE";

export interface ITechnicianContactsState {
  contacts: any[];

  /*
    {
        <contactId> : <counterValue>,
        <contactId> : <counterValue>,
        ...
        <contactId> : <counterValue>,
    }
    */
  incomingCounters: any;
  allTechIncomingMessages: number;

  activeContact: any;
  activeContactIndex: number;
  serachPhrase: string;
  visibilityChat: boolean;
  chat: any[];
  chatLoadingIndicator: boolean;
}

const initialState: ITechnicianContactsState = {
  contacts: [],
  incomingCounters: {},
  allTechIncomingMessages: 0,
  activeContactIndex: -1,
  activeContact: null,
  serachPhrase: "",
  visibilityChat: false,
  chat: [],
  chatLoadingIndicator: false,
};

const sortContactFunc = function (a: any, b: any) {
  const aDate = a.original.msg_date || null;
  const bDate = b.original.msg_date || null;

  if (aDate === null && bDate === null) return 0;
  if (aDate !== null && bDate === null) return -1;
  if (aDate === null && bDate !== null) return 1;

  if (moment(aDate).isAfter(bDate)) return -1;
  if (moment(aDate).isBefore(bDate)) return 1;

  return 0;
};

export class TechnicianContactsActions {
  static loadContacts = (contacts: any[]): Action => ({
    type: LOAD_TECHNICIAN_CONTACTS,
    payload: { contacts: contacts },
  });
  static setActiveContact = (contact: any, index: number): Action => ({
    type: SET_ACTIVE_TECHNICIAN_CONTACT,
    payload: { contact: contact, index: index },
  });
  static setActiveBySystemId = (systemId: number): Action => ({
    type: SET_ACTIVE_TECH_CONTACT_BY_SYSTEM_ID,
    payload: { systemId: systemId },
  });
  static typeSearch = (phrase: string): Action => ({
    type: TYPE_TECHNICIAN_SEARCH_STRING,
    payload: { phrase: phrase },
  });
  static changeVisibility = (): Action => ({
    type: CHANGE_VISIBILITY,
    payload: {},
  });
  static loadMessages = (messagesForContact: {
    contact: any;
    messages: any[];
  }): Action => ({
    type: TECHNICIAN_LOAD_MESSAGES,
    payload: { messagesForContact: messagesForContact },
  });
  static messageWasSent = (): Action => ({
    type: MESSAGE_WAS_SENT,
    payload: {},
  });
  static updateIncomingCounter = (incomingMessage: any): Action => ({
    type: TECH_UPDATE_INCOMING_COUNTER,
    payload: { incomingMessage },
  });
  static technicianUpdateMessageStatus = (updatedMessage: any): Action => ({
    type: TECHNICIAN_UPDATE_MESSAGE_STATUS,
    payload: { updatedMessage },
  });
}

export function reduce(
  state: ITechnicianContactsState = initialState,
  action: Action
) {
  switch (action.type) {
    case MESSAGE_WAS_SENT: {
      return {
        ...state,
        ...{
          chatLoadingIndicator: true,
        },
      };
    }

    // Reset contacts selection by change contact type
    case CHANGE_CONTACT_TYPE: {
      const contacts = state.contacts.map((it) => ({ ...it, active: false }));
      return {
        ...state,
        ...{
          chat: [],
          contacts: contacts,
          activeContact: null,
          activeContactIndex: -1,
          chatLoadingIndicator: false,
        },
      };
    }

    case TECHNICIAN_LOAD_MESSAGES: {
      const { messages, contact } = action.payload?.messagesForContact;
      const targetContactIndex = state.contacts.findIndex(
        (it) => it.phone === contact.phone
      );
      const targetContactID = state.contacts[targetContactIndex]?.original?.id;

      if (targetContactIndex >= 0) {
        const lastMessage = messages[messages.length - 1] || {};
        const newContacts = state.contacts
          .map((it, index) => {
            if (index === targetContactIndex) {
              it.message = lastMessage.body || "";
              it.original.message = lastMessage.body || "";
              it.original.msg_date = lastMessage.date || "";

              return it;
            }
            return it;
          })
          .sort(sortContactFunc);

        const index = findActiveContactIndex(newContacts, targetContactID);

        return {
          ...state,
          ...{
            contacts: newContacts,
            chat: messages,

            // In case with searching contacts - we don't have ids for them and can't calculate index
            activeContactIndex: index >= 0 ? index : state.activeContactIndex,
            chatLoadingIndicator: false,
          },
        };
      }

      return state;
    }

    case TECHNICIAN_UPDATE_MESSAGE_STATUS: {
      const updatedMessage = action.payload?.updatedMessage || {};
      const start = state.chat.length - 1;
      const end = 0;
      for (let i = start; i >= end; i--) {
        if (state.chat[i].id === updatedMessage.id) {
          state.chat[i].status = updatedMessage.status;
          break;
        }
      }

      return {
        ...state,
        ...{
          chat: state.chat.map((it) => ({ ...it })),
        },
      };
    }

    case LOAD_TECHNICIAN_CONTACTS: {
      const contacts = action.payload?.contacts;
      const list = contacts
        .filter(
          (it: any) =>
            !isNumber(it.type) ||
            (isNumber(it.type) && it.type === ContactTypes.TECHNICIAN)
        )
        .map((it: any) => ({
          avatar: it.avatar,
          name: `${it.first_name} ${it.last_name}`,
          datetime: "--:-- AM",
          phone: it.phone,
          message: it.message,
          original: it,
          type: ContactTypes.TECHNICIAN,
        }))
        .sort(sortContactFunc);

      return {
        ...state,
        ...{
          contacts: list,
        },
      };
    }

    case SET_ACTIVE_TECHNICIAN_CONTACT: {
      const index = action.payload?.index;
      const incomingCounters = state.incomingCounters;
      const ln = state.contacts.length;
      let activeContact = null;
      const contacts = state.contacts.map((it, idx) => {
        if (idx === index) {
          activeContact = it;
          incomingCounters[it.original.id] = 0;
        }
        return {
          ...it,
          active: idx === index,
        };
      });
      const allTechIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allTechIncomingMessages: allTechIncomingMessages,
          activeContact: activeContact,
          activeContactIndex: index,
          chatLoadingIndicator: true,
        },
      };
    }

    case SET_ACTIVE_TECH_CONTACT_BY_SYSTEM_ID: {
      const systemId = action.payload?.systemId;
      const incomingCounters = state.incomingCounters;
      let index = null;
      let activeContact = null;
      const contacts = state.contacts.map((it, idx) => {
        if (it.original.system_item_id === systemId) {
          activeContact = it;
          incomingCounters[it.original.id] = 0;
          index = idx;
        }
        return {
          ...it,
          active: it.original.system_item_id === systemId,
        };
      });
      const allTechIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allTechIncomingMessages: allTechIncomingMessages,
          activeContact: activeContact,
          activeContactIndex: index,
          chatLoadingIndicator: false,
        },
      };
    }

    case TYPE_TECHNICIAN_SEARCH_STRING: {
      return {
        ...state,
        ...{
          serachPhrase: action.payload?.phrase || "",
        },
      };
    }

    case CHANGE_VISIBILITY: {
      return {
        ...state,
        ...{
          visibilityChat: !state.visibilityChat,
        },
      };
    }

    case TECH_UPDATE_INCOMING_COUNTER: {
      const incomingMessage = action.payload?.incomingMessage;
      const incSenderPhone = incomingMessage.sender;
      const incomingCounters = state.incomingCounters;
      const contacts = state.contacts
        .map((it, idx) => {
          if (it.phone === incSenderPhone) {
            incomingCounters[it.original.id] =
              (incomingCounters[it.original.id] || 0) + 1;
            it.message = incomingMessage.body || "";
            it.original.message = incomingMessage.body || "";
            it.original.msg_date = incomingMessage.date || "";
            return it;
          }
          return it;
        })
        .sort(sortContactFunc);

      const allTechIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allTechIncomingMessages: allTechIncomingMessages,
        },
      };
    }

    default:
      return state;
  }
}
